import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Group,
  SegmentedControl,
  Stack,
  Table,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { Link, useNavigate, useParams } from 'react-router';
import {
  KURUL_GUNDEM_ILK_SIRA,
  KURUL_GUNDEM_OTOMATIK_ETIKETI,
  KURUL_KACINMA_TALEBI_ETIKETI,
  KURUL_PLANLAMA_NOTU_MAX,
  KURUL_TOPLANTI_TIP_ETIKETI,
} from '@isg/validation';
import type { KurulToplantiTipi } from '@isg/validation';
import { useKurul, useToplantiPlanla } from '../api/queries';
import { QueryStatus } from './parts';
import { ANLAM } from '../theme';
import { gunGirdisiIso } from '../lib/format';
import { useVarsayilanGun } from '../lib/saha-gunu';

/**
 * TOPLANTI PLANLAMA — AYRI SAYFA (P1; Kapsam §28/C5). Tip · tarih · saat ·
 * yer · serbest gündem. Davet kayıt anında üyelere gider; 48 saat bir
 * UYARIDIR (kapı değil) — toplantı sayfasında görünür.
 */
export function KurulToplantiYeniScreen(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const kurul = useKurul(id);
  const planla = useToplantiPlanla();
  const navigate = useNavigate();
  const [tip, setTip] = useState<KurulToplantiTipi>('olagan');
  // G3.3 — kaçınma talebi yalnız olağanüstüde işaretlenir (tip değişince düşer).
  const [kacinmaTalebi, setKacinmaTalebi] = useState(false);
  // G3.4 — planlama notu (tutanağa girer).
  const [planlamaNotu, setPlanlamaNotu] = useState('');
  // Madde 302: varsayılan "bugün" SAHA GÜNÜ (sunucudan) — kullanıcı değiştirir.
  const [tarih, setTarih] = useVarsayilanGun();
  const [baslangic, setBaslangic] = useState('');
  const [bitis, setBitis] = useState('');
  const [yer, setYer] = useState('');
  const [gundem, setGundem] = useState<string[]>([]);
  const [yeniMadde, setYeniMadde] = useState('');
  const engel = gunGirdisiIso(tarih) === null ? 'Geçerli bir tarih girin' : null;
  return (
    <Stack data-testid="kurul-toplanti-yeni-sayfasi">
      <Link to={`/isg-kurulu/${id}`} data-testid="detay-geri">
        ← Kurul
      </Link>
      <Title order={3}>Toplantı planla</Title>
      <QueryStatus queries={[kurul]} />
      {planla.error && (
        <Alert color={ANLAM.engel} role="alert" data-testid="kurul-toplanti-yeni-hata">
          {planla.error.message}
        </Alert>
      )}
      <Card withBorder>
        <Stack>
          {/* G3.3 — TEK EKRAN + tip seçimi (üç sekme yerine): PLANLI · OLAĞANÜSTÜ;
              olağanüstüde "çalışmaktan kaçınma talebi" işareti. */}
          <Group align="center" gap="md" wrap="wrap">
            <SegmentedControl
              data-testid="kurul-toplanti-tip"
              value={tip}
              onChange={(v) => {
                setTip(v as KurulToplantiTipi);
                if (v !== 'olaganustu') setKacinmaTalebi(false);
              }}
              data={Object.entries(KURUL_TOPLANTI_TIP_ETIKETI).map(([value, label]) => ({
                value,
                label,
              }))}
            />
            {tip === 'olaganustu' && (
              <Checkbox
                label={KURUL_KACINMA_TALEBI_ETIKETI}
                data-testid="kurul-toplanti-kacinma-talebi"
                checked={kacinmaTalebi}
                onChange={(e) => setKacinmaTalebi(e.currentTarget.checked)}
              />
            )}
          </Group>
          <Group align="flex-end" wrap="wrap">
            <TextInput
              w={170}
              type="date"
              label="Tarih"
              data-testid="kurul-toplanti-tarih"
              value={tarih}
              onChange={(e) => setTarih(e.currentTarget.value)}
            />
            <TextInput
              w={120}
              type="time"
              label="Başlangıç"
              data-testid="kurul-toplanti-baslangic"
              value={baslangic}
              onChange={(e) => setBaslangic(e.currentTarget.value)}
            />
            <TextInput
              w={120}
              type="time"
              label="Bitiş"
              data-testid="kurul-toplanti-bitis"
              value={bitis}
              onChange={(e) => setBitis(e.currentTarget.value)}
            />
            <TextInput
              w={280}
              label="Yer"
              data-testid="kurul-toplanti-yer"
              value={yer}
              onChange={(e) => setYer(e.currentTarget.value)}
            />
          </Group>
          <Table data-testid="kurul-toplanti-gundem-tablosu">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={40}>#</Table.Th>
                <Table.Th>Gündem maddesi</Table.Th>
                <Table.Th w={80} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {/* G3.2 — 1. madde OTOMATİKTİR (önceki kararların durumu, türetilir);
                  soluk/salt okunur satır olarak görünür ki numara atlamış sanılmasın. */}
              <Table.Tr data-testid="kurul-toplanti-gundem-otomatik" style={{ opacity: 0.55 }}>
                <Table.Td>1</Table.Td>
                <Table.Td>{KURUL_GUNDEM_OTOMATIK_ETIKETI}</Table.Td>
                <Table.Td />
              </Table.Tr>
              {gundem.map((g, i) => (
                <Table.Tr key={`${i}-${g}`}>
                  <Table.Td>{i + KURUL_GUNDEM_ILK_SIRA}</Table.Td>
                  <Table.Td>{g}</Table.Td>
                  <Table.Td>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      onClick={() => setGundem((l) => l.filter((_, j) => j !== i))}
                    >
                      Sil
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
              <Table.Tr>
                <Table.Td />
                <Table.Td>
                  <TextInput
                    size="xs"
                    placeholder="+ Gündem maddesi"
                    aria-label="Yeni gündem maddesi"
                    data-testid="kurul-toplanti-gundem-girdi"
                    value={yeniMadde}
                    onChange={(e) => setYeniMadde(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && yeniMadde.trim()) {
                        setGundem((l) => [...l, yeniMadde.trim()]);
                        setYeniMadde('');
                      }
                    }}
                  />
                </Table.Td>
                <Table.Td>
                  <Button
                    size="compact-xs"
                    data-testid="kurul-toplanti-gundem-ekle"
                    disabled={!yeniMadde.trim()}
                    onClick={() => {
                      setGundem((l) => [...l, yeniMadde.trim()]);
                      setYeniMadde('');
                    }}
                  >
                    Ekle
                  </Button>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
          <Textarea
            label="Planlama notu"
            data-testid="kurul-toplanti-planlama-notu"
            autosize
            minRows={2}
            maxLength={KURUL_PLANLAMA_NOTU_MAX}
            value={planlamaNotu}
            onChange={(e) => setPlanlamaNotu(e.currentTarget.value)}
          />
          <Group justify="flex-end" align="center">
            {engel && (
              <span
                style={{ fontSize: 12, opacity: 0.7 }}
                data-testid="kurul-toplanti-kaydet-engeli"
              >
                {engel}
              </span>
            )}
            <Button
              data-testid="kurul-toplanti-kaydet"
              disabled={engel !== null || !id}
              loading={planla.isPending}
              onClick={() =>
                id &&
                planla.mutate(
                  {
                    kurulId: id,
                    girdi: {
                      tip,
                      kacinmaTalebi,
                      planlamaNotu: planlamaNotu.trim() || null,
                      tarih,
                      baslangicSaat: baslangic || null,
                      bitisSaat: bitis || null,
                      yer: yer.trim() || null,
                      gundem: gundem.map((baslik) => ({ baslik })),
                    },
                  },
                  { onSuccess: (t) => void navigate(`/isg-kurulu/toplanti/${t.id}`) },
                )
              }
            >
              Planla ve davet gönder
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
